import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { TableCell, TableRow } from "@shadcn-in-nx/ui/table";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import BackfillStatusBadge from "./BackfillStatusBadge";

export default function CompanyRow({ company, onComplete }) {
    const { t } = useTranslation();
    const [status, setStatus] = useState(company.backfillStatus);
    const { call: triggerBackfill, isLoading } = useBluelagoon({ method: "triggerCompanyBackfill" });

    const handleBackfill = useCallback(async () => {
        setStatus("in_progress");
        try {
            const force = status === "failed";
            const res = await triggerBackfill({ companyId: company.companyId, force });
            if (res?.data?.alreadyCompleted) {
                toast.info(t("adminDataMigration.backfillAlreadyDone"));
                setStatus("completed");
            } else if (res?.success) {
                toast.success(`${t("adminDataMigration.backfillSuccess")}: ${res.data.records} ${t("adminDataMigration.records")}, ${res.data.tables} ${t("adminDataMigration.tables")}`);
                setStatus("completed");
            } else {
                toast.error(res?.error || t("adminDataMigration.backfillFailed"));
                setStatus("failed");
            }
            onComplete();
        } catch (err) {
            toast.error(err?.error || t("adminDataMigration.backfillFailed"));
            setStatus("failed");
        }
    }, [company.companyId, status, triggerBackfill, onComplete, t]);

    const isDone = status === "completed";
    const isBusy = isLoading || status === "in_progress";
    const canRetry = status === "failed";
    const details = company.details;
    const isLarge = company.sizeClass === "large";

    return (
        <TableRow>
            <TableCell className="font-medium">{company.companyId}</TableCell>
            <TableCell>{company.name}</TableCell>
            <TableCell>
                <BackfillStatusBadge status={status} />
                {details?.completedTablesCount > 0 && status !== "completed" && (
                    <span className="text-xs text-muted-foreground ml-2">
                        {details.completedTablesCount} {t("adminDataMigration.tables")}
                    </span>
                )}
            </TableCell>
            <TableCell className="text-center">
                <span>{(company.totalRecords || 0).toLocaleString()}</span>
                {isLarge && (
                    <Badge variant="outline" className="ml-2 text-xs">{t("adminDataMigration.largeCompany")}</Badge>
                )}
            </TableCell>
            <TableCell className="text-center">{company.databaseCount}</TableCell>
            <TableCell>
                {details?.error && <p className="text-xs text-red-500 mb-1 max-w-48 truncate" title={details.error}>{details.error}</p>}
                <Button size="sm" variant={isDone ? "outline" : canRetry ? "destructive" : "default"} disabled={isBusy || isDone} onClick={handleBackfill}>
                    {isBusy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    {canRetry ? t("adminDataMigration.retry") : t("adminDataMigration.backfill")}
                </Button>
            </TableCell>
        </TableRow>
    );
}
